import type { InfiniteTablePropColumns } from "@infinite-table/infinite-react";
import type { BacktestItem } from "epsm-api-types/models";
import type { Dispatch, SetStateAction } from "react";
import newsConfig from "@/components/widgets/Backtest/config.ts";
import { getClassNameForProfitAndLoss } from "@/components/widgets/Backtest/helpers/getClassNameForProfitAndLoss.ts";
import {
	formatValueCurrency,
	formatValueDate,
} from "@/components/widgets/helpers/FormattingHelpers.ts";
import { getAlign } from "@/lib/utils";

const { DEFAULT_COLUMN_ORDER } = newsConfig;

export const getColumns = (
	setColumnOrder: Dispatch<SetStateAction<string[]>>,
): InfiniteTablePropColumns<BacktestItem> => {
	const columns: InfiniteTablePropColumns<BacktestItem> = {
		date: {
			defaultFilterable: true,
			header: "Date",
			field: "date",
			type: "date",
			headerAlign: "start",
			align: getAlign("date"),
			valueGetter: ({ data }) => formatValueDate(data.date),
		},
		ticker: {
			defaultFilterable: true,
			defaultWidth: 42,
			header: "Ticker",
			field: "ticker",
			headerAlign: "start",
			align: getAlign("str"),
		},
		action: {
			header: "Action",
			field: "action_type",
			headerAlign: "start",
			align: getAlign("str"),
			valueGetter: ({ data }) => data.action_type.toUpperCase(),
		},
		price: {
			header: "Price",
			field: "adj_close",
			headerAlign: "start",
			align: getAlign("str"),
			valueGetter: ({ data }) => formatValueCurrency(data.adj_close),
		},
		quantity: {
			header: "Quantity",
			field: "adj_count",
			headerAlign: "start",
			align: getAlign("num"),
		},
		totalQuantity: {
			header: "Total quantity",
			field: "adj_total_shares",
			headerAlign: "start",
			align: getAlign("num"),
			valueGetter: ({ data }) => data.adj_total_shares,
		},
		profitAndLoss: {
			header: "Profit and loss",
			field: "profit_and_loss",
			headerAlign: "start",
			align: getAlign("num"),
			className: ({ data }) =>
				getClassNameForProfitAndLoss(data?.profit_and_loss || 0),
			valueGetter: ({ data }) => formatValueCurrency(data.profit_and_loss),
		},
		amountOfMoney: {
			header: "Amount of money",
			field: "amount_of_money",
			headerAlign: "start",
			align: getAlign("num"),
			valueGetter: ({ data }) => formatValueCurrency(data.amount_of_money),
		},
		capital: {
			header: "Capital",
			field: "cash",
			headerAlign: "start",
			align: getAlign("num"),
			valueGetter: ({ data }) => formatValueCurrency(data.cash),
		},
	};

	const columnKeys = Object.keys(columns);
	const allKeysCombined = columnKeys.concat(DEFAULT_COLUMN_ORDER);
	const uniqueColumnOrder = [...new Set(allKeysCombined)];

	setColumnOrder(uniqueColumnOrder);
	return columns;
};
